import React, {useEffect, useState} from 'react';
import {Alert, Badge, Button, Drawer, Select, Space, Typography} from 'antd';
import {FilterOutlined} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {fetchFilterData, invalidateFilterData} from '../services/filterData';

const FIELDS = [
  {field: 'publisher', label: 'Publisher'},
  {field: 'provider', label: 'Platform'},
  {field: 'website', label: 'Website'},
  {field: 'label', label: 'Label'},
];

const DashboardFilterSelector = ({rules, onApply, publisherView, partition, hideProviders}) => {
  const auth = useSelector(state => state.authReducer);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(rules);
  const [resources, setResources] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const fields = FIELDS.filter(({field}) => field !== 'provider' || !hideProviders);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetchFilterData(auth, {publisherView, partition})
      .then(data => {
        if (!data?.success) throw new Error('Invalid filter response');
        if (active) setResources(data);
      })
      .catch(() => {if (active) setError('Could not load filter values. Please retry.');})
      .finally(() => {if (active) setLoading(false);});
    return () => {active = false;};
  }, [publisherView, partition, retry, auth]);

  const updateRule = (field, changes) => {
    setDraft(previous => {
      const current = previous.find(rule => rule.field === field)
        || {field, operator: 'include', values: []};
      return [...previous.filter(rule => rule.field !== field), {...current, ...changes}];
    });
  };

  const apply = () => {
    // Fixed category/value order avoids requests when only selection order changes.
    onApply(fields.map(({field}) => draft.find(rule => rule.field === field))
      .filter(rule => rule?.values.length)
      .map(rule => ({...rule, values: [...new Set(rule.values)].sort()})));
    setOpen(false);
  };

  return (
    <>
      <Badge count={rules.length} size="small">
        <Button icon={<FilterOutlined />} onClick={() => {setDraft(rules); setOpen(true);}}>
          Advanced filters
        </Button>
      </Badge>
      <Drawer title="Dashboard filters" open={open} width={560} onClose={() => setOpen(false)}
        extra={<Button onClick={() => {onApply([]); setDraft([]); setOpen(false);}}>Reset</Button>}
        footer={<Space><Button type="primary" onClick={apply} disabled={loading || !!error}>Apply</Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button></Space>}>
        <Space direction="vertical" size="large" style={{width: '100%'}}>
          <Typography.Text type="secondary">
            Include any selected value within a category. All categories apply together.
            Empty selections do not filter the report. These rules also apply together with the quick filter.
          </Typography.Text>
          {error && <Alert type="error" message={error} action={
            <Button size="small" onClick={() => {invalidateFilterData(); setRetry(value => value + 1);}}>Retry</Button>
          } />}
          {fields.map(({field, label}) => {
            const rule = draft.find(item => item.field === field);
            const options = (resources[field] || [])
              .filter(value => typeof value === 'string' && value !== 'Others' && value !== '')
              .map(value => ({label: value, value}));
            return (
              <div key={field}>
                <Typography.Text strong>{label}</Typography.Text>
                <div style={{display: 'flex', gap: 8, marginTop: 8}}>
                  <Select aria-label={`${label} operation`} value={rule?.operator || 'include'}
                    style={{width: 110, flexShrink: 0}} disabled={loading || !!error}
                    options={[{value: 'include', label: 'Include'}, {value: 'exclude', label: 'Exclude'}]}
                    onChange={operator => updateRule(field, {operator})} />
                  <Select aria-label={`${label} values`} mode="multiple" maxCount={100} allowClear showSearch
                    value={rule?.values || []} options={options} loading={loading}
                    disabled={loading || !!error} optionFilterProp="label" maxTagCount="responsive"
                    style={{flex: 1, minWidth: 0}} placeholder={`Select ${label.toLowerCase()} values`}
                    onChange={values => updateRule(field, {values})} />
                </div>
              </div>
            );
          })}
        </Space>
      </Drawer>
    </>
  );
};

export default DashboardFilterSelector;
